import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { CalendarIcon, Archive, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PurchaseReviewModal } from "./PurchaseReviewModal";

interface PurchaseRequest {
  id: string;
  listing_id: string;
  buyer_id: string;
  seller_id: string;
  quantity: number;
  total_price: number;
  delivery_method: string;
  pickup_date: string | null;
  pickup_time: string | null;
  shipping_address: string | null;
  status: string;
  proposed_pickup_date: string | null;
  proposed_pickup_time: string | null;
  created_at: string;
  listing_title?: string;
  listing_image?: string | null;
  listing_category?: string | null;
  pickup_address?: string | null;
  other_user_name?: string;
  other_user_email?: string;
  other_user_avatar?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: PurchaseRequest | null;
  currentUserId: string;
  onUpdated: () => void;
}

export function PurchaseRequestDetailModal({ open, onOpenChange, request, currentUserId, onUpdated }: Props) {
  const navigate = useNavigate();
  const [showPropose, setShowPropose] = useState(false);
  const [proposedDate, setProposedDate] = useState<Date | undefined>();
  const [proposedTime, setProposedTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasReviewed, setHasReviewed] = useState<boolean | null>(null);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  useEffect(() => {
    if (!request || request.status !== "completed") {
      setHasReviewed(null);
      return;
    }
    const otherUserId = currentUserId === request.seller_id ? request.buyer_id : request.seller_id;
    const checkReview = async () => {
      const { data } = await supabase
        .from("purchase_reviews")
        .select("id")
        .eq("author_id", currentUserId)
        .eq("target_user_id", otherUserId)
        .maybeSingle();
      setHasReviewed(!!data);
    };
    checkReview();
  }, [request?.id, request?.status, request?.buyer_id, request?.seller_id, currentUserId]);

  if (!request) return null;

  const isSeller = currentUserId === request.seller_id;
  const isBuyer = currentUserId === request.buyer_id;

  // Determine the most recent date/time values
  const displayDate = request.proposed_pickup_date || request.pickup_date;
  const displayTime = request.proposed_pickup_time || request.pickup_time;

  const reduceListingQuantity = async () => {
    const { data: listing } = await supabase
      .from("listings")
      .select("quantity")
      .eq("id", request.listing_id)
      .single();
    if (!listing) return;
    const newQty = Math.max(0, (listing.quantity ?? 0) - request.quantity);
    const updateData: any = { quantity: newQty, updated_at: new Date().toISOString() };
    if (newQty <= 0) updateData.status = "såld";
    await supabase.from("listings").update(updateData).eq("id", request.listing_id);
  };

  const sendAcceptedSystemMessage = async () => {
    if (!request) return;
    const isPickup = request.delivery_method === "pickup";
    const date = request.proposed_pickup_date || request.pickup_date;
    const time = request.proposed_pickup_time || request.pickup_time;
    const address = isPickup ? request.pickup_address || null : request.shipping_address || null;

    const payload = {
      type: "purchase_accepted",
      version: 1,
      delivery_method: request.delivery_method,
      total_price: request.total_price,
      quantity: request.quantity,
      address,
      date,
      time,
      listing_title: request.listing_title || null,
    };

    const threadId = `listing-${request.listing_id}-${request.buyer_id}`;
    const { error: msgError } = await supabase.from("messages").insert({
      thread_id: threadId,
      sender_id: request.seller_id,
      content: `__SYSTEM__:${JSON.stringify(payload)}`,
    });
    if (msgError) {
      console.error("Failed to send accepted system message", msgError);
    }
  };

  const handleAction = async (newStatus: string) => {
    setLoading(true);
    const { error } = await supabase
      .from("purchase_requests")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", request.id);
    setLoading(false);
    if (error) {
      toast.error("Kunde inte uppdatera förfrågan");
    } else {
      if (newStatus === "accepted") {
        await reduceListingQuantity();
        await sendAcceptedSystemMessage();
      }
      const msg = newStatus === "accepted" ? "Köpförslag accepterat!" : "Köpförslag nekat.";
      toast.success(msg);
      onUpdated();
      onOpenChange(false);
    }
  };

  const handleArchive = async () => {
    setLoading(true);
    const isBuyer = request.buyer_id === currentUserId;
    const archiveField = isBuyer ? "archived_by_buyer" : "archived_by_seller";
    const { error } = await supabase
      .from("purchase_requests")
      .update({ [archiveField]: true, updated_at: new Date().toISOString() })
      .eq("id", request.id);
    setLoading(false);
    if (error) {
      toast.error("Kunde inte arkivera förfrågan");
    } else {
      toast.success("Köpförslag arkiverat!");
      onUpdated();
      onOpenChange(false);
    }
  };

  const handlePropose = async () => {
    if (!proposedDate || !proposedTime) return;
    setLoading(true);
    const updateData: any = {
      proposed_pickup_date: format(proposedDate, "yyyy-MM-dd"),
      proposed_pickup_time: proposedTime,
      status: "proposed",
      updated_at: new Date().toISOString(),
    };
    const { error } = await supabase
      .from("purchase_requests")
      .update(updateData)
      .eq("id", request.id);
    setLoading(false);
    if (error) {
      toast.error("Kunde inte skicka ändring");
    } else {
      toast.success("Nytt förslag skickat!");
      setShowPropose(false);
      onUpdated();
      onOpenChange(false);
    }
  };

  const deliveryLabel = request.delivery_method === "pickup" ? "Upphämtning" : "Frakt";
  const deliverySub = request.delivery_method === "pickup"
    ? "Upphämtning hos säljaren"
    : request.shipping_address || "Frakt";

  // Format display date nicely
  const formattedDate = displayDate
    ? new Date(displayDate + "T00:00:00").toLocaleDateString("sv-SE", { day: "numeric", month: "long" })
    : null;

  const canArchive = request.status === "accepted" || request.status === "denied" || request.status === "completed";
  const buyerCanRespond = isBuyer && request.status === "proposed";
  const canMarkCompleted = isSeller && request.status === "accepted";

  const handleMarkCompleted = async () => {
    setLoading(true);
    const { error } = await supabase
      .from("purchase_requests")
      .update({ status: "completed", updated_at: new Date().toISOString() })
      .eq("id", request.id);
    setLoading(false);
    if (error) {
      toast.error("Kunde inte uppdatera status");
    } else {
      toast.success("Affären har markerats som slutförd!");
      onUpdated();
      onOpenChange(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setShowPropose(false); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Köpförslag</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Sender info */}
          <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3">
            <div className="h-11 w-11 shrink-0 rounded-lg overflow-hidden bg-muted">
              {request.other_user_avatar ? (
                <img src={request.other_user_avatar} alt={request.other_user_name} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-sm font-semibold text-muted-foreground">
                  {request.other_user_name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                </div>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{request.other_user_name}</p>
              <p className="text-xs text-muted-foreground truncate">{request.other_user_email}</p>
            </div>
          </div>

          {/* Listing preview */}
          <button
            onClick={() => { onOpenChange(false); navigate(`/annonser/${request.listing_id}`); }}
            className="flex w-full items-center gap-3 rounded-lg border border-border bg-muted/30 p-3 text-left transition-colors hover:bg-muted/50"
          >
            <div className="h-11 w-11 shrink-0 rounded-lg overflow-hidden bg-muted">
              {request.listing_image ? (
                <img src={request.listing_image} alt={request.listing_title} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-muted-foreground text-xs">—</div>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{request.listing_title}</p>
              {request.listing_category && (
                <p className="text-xs text-muted-foreground truncate">{request.listing_category}</p>
              )}
            </div>
          </button>
          {/* Summary container */}
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <div className="flex">
              {/* Left: Price & quantity */}
              <div className="flex-1 space-y-1">
                <p className="text-xl font-bold text-foreground">
                  {request.total_price.toLocaleString("sv-SE")} kr
                </p>
                <p className="text-sm text-muted-foreground">{request.quantity} st</p>
              </div>

              {/* Divider */}
              <Separator orientation="vertical" className="mx-4 h-auto" />

              {/* Right: Date, time & address */}
              <div className="flex-1 space-y-1">
                {request.proposed_pickup_date && (
                  <span className="inline-block rounded bg-yellow-500 px-1.5 py-0.5 text-[10px] font-semibold text-white mb-1">
                    Ny föreslagen tid
                  </span>
                )}
                <p className="text-xl font-bold text-foreground">
                  {formattedDate ? `${formattedDate}${displayTime ? `, ${displayTime}` : ""}` : deliveryLabel}
                </p>
                <p className="text-sm text-muted-foreground">{deliverySub}</p>
              </div>
            </div>
          </div>

          {/* Pickup address – revealed only when accepted */}
          {request.status === "accepted" && request.delivery_method === "pickup" && request.pickup_address && (
            <div className="rounded-lg border-2 border-green-600 bg-muted/30 p-4">
              <p className="text-xs font-medium text-muted-foreground mb-1">Upphämtningsadress</p>
              <p className="text-base font-semibold text-foreground">{request.pickup_address}</p>
            </div>
          )}

          {/* Details */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Skickat</span>
              <span className="font-medium">{new Date(request.created_at).toLocaleDateString("sv-SE")}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Leveransmetod</span>
              <span className="font-medium">{deliveryLabel}</span>
            </div>
            {request.delivery_method === "pickup" && request.pickup_date && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Önskad upphämtning</span>
                <span className="font-medium">{request.pickup_date} {request.pickup_time}</span>
              </div>
            )}
            {request.delivery_method === "shipping" && request.shipping_address && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Leveransadress</span>
                <span className="font-medium text-right max-w-[200px]">{request.shipping_address}</span>
              </div>
            )}
            {request.proposed_pickup_date && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Säljarens förslag</span>
                <span className="font-medium">{request.proposed_pickup_date} {request.proposed_pickup_time}</span>
              </div>
            )}
          </div>

          {/* Completed state – review button or already-reviewed label */}
          {request.status === "completed" && (
            <>
              {hasReviewed === false && (
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => {
                    onOpenChange(false);
                    setShowReviewModal(true);
                  }}
                >
                  Lämna omdöme
                </Button>
              )}
              {hasReviewed === true && (
                <p className="w-full text-center text-sm text-muted-foreground py-2">
                  Du har redan lämnat ett omdöme på den här användaren.
                </p>
              )}
            </>
          )}

          {/* Mark as completed – seller only, accepted status */}
          {request.status !== "completed" && canMarkCompleted && !showPropose && (
            <Button
              variant="outline"
              className="w-full gap-2 border-green-600 text-green-700 hover:bg-green-50 dark:text-green-400 dark:border-green-500 dark:hover:bg-green-950"
              disabled={loading}
              onClick={() => {
                onOpenChange(false);
                setShowCompleteConfirm(true);
              }}
            >
              <CheckCircle2 className="h-4 w-4" />
              Markera som slutförd
            </Button>
          )}

          {/* Seller actions – pending */}
          {request.status !== "completed" && isSeller && request.status === "pending" && !showPropose && (
            <div className="flex flex-col gap-2 pt-2">
              <Button className="bg-primary hover:bg-primary/90" disabled={loading} onClick={() => handleAction("accepted")}>
                Acceptera
              </Button>
              <Button variant="outline" disabled={loading} onClick={() => setShowPropose(true)}>
                Ändra förslag
              </Button>
              <Button variant="destructive" disabled={loading} onClick={() => handleAction("denied")}>
                Neka
              </Button>
            </div>
          )}

          {/* Buyer actions – proposed (seller proposed new time) */}
          {request.status !== "completed" && buyerCanRespond && !showPropose && (
            <div className="flex flex-col gap-2 pt-2">
              <Button className="bg-primary hover:bg-primary/90" disabled={loading} onClick={() => handleAction("accepted")}>
                Acceptera
              </Button>
              <Button variant="outline" disabled={loading} onClick={() => setShowPropose(true)}>
                Föreslå ny tid
              </Button>
              <Button variant="destructive" disabled={loading} onClick={() => handleAction("denied")}>
                Neka
              </Button>
            </div>
          )}

          {/* Propose new date (seller or buyer) */}
          {request.status !== "completed" && showPropose && (
            <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
              <p className="text-sm font-medium">Föreslå nytt datum & tid</p>
              <div>
                <Label>Datum</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("w-full justify-start text-left font-normal", !proposedDate && "text-muted-foreground")}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {proposedDate ? format(proposedDate, "PPP", { locale: sv }) : "Välj datum"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[1100]" align="start">
                    <Calendar
                      mode="single"
                      selected={proposedDate}
                      onSelect={setProposedDate}
                      disabled={(date) => date < new Date()}
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Tid</Label>
                <Input type="time" value={proposedTime} onChange={(e) => setProposedTime(e.target.value)} />
              </div>
              <div className="flex gap-2">
                <Button className="flex-1" disabled={!proposedDate || !proposedTime || loading} onClick={handlePropose}>
                  Skicka förslag
                </Button>
                <Button variant="ghost" onClick={() => setShowPropose(false)}>Avbryt</Button>
              </div>
            </div>
          )}

          {/* Archive button */}
          {canArchive && (
            <Button
              variant="outline"
              className="w-full gap-2 mt-2"
              disabled={loading}
              onClick={handleArchive}
            >
              <Archive className="h-4 w-4" />
              Arkivera
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>

      <AlertDialog open={showCompleteConfirm} onOpenChange={setShowCompleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Markera som slutförd</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill markera den här affären som slutförd?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkCompleted}>Bekräfta</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {request && (
        <PurchaseReviewModal
          open={showReviewModal}
          onOpenChange={setShowReviewModal}
          purchaseRequestId={request.id}
          authorId={currentUserId}
          targetUserId={currentUserId === request.buyer_id ? request.seller_id : request.buyer_id}
          targetUserName={request.other_user_name || "Användare"}
          onReviewSubmitted={() => {
            setHasReviewed(true);
            onUpdated();
          }}
        />
      )}
    </>
  );
}
